import { LeaveType, NoLocationPolicy, OutOfBoundsPolicy, QuotaType, QuotaUnit, Shift, Timesheet, UserFaceImage, WeekSchedule, Workplace, WorkplaceConfig } from "./attendance";
import { UserEducationView } from "./employee";
import { UserClaimAttachment } from "./payroll";

// ..\mowazaf\Data\General\Entities\Attachment.cs
export interface Attachment extends Record {
    id: number;
    fileName: string;
    fileExtention: string;
    filePath: string;
    fileSize: number;
    fileType?: FileType;
    fileDescription?: string;
    userAttachmentAccesses?: UserAttachmentAccess[];
}

// ..\mowazaf\Data\General\Entities\Attachment.cs
export enum FileType {
    Image = 'Image',
    Document = 'Document',
    Video = 'Video',
    Audio = 'Audio',
    Other = 'Other',
}

// ..\mowazaf\Data\General\Entities\Bank.cs
export interface Bank extends Record {
    id: number;
    name: string;
    routingNumber: string;
    companyId?: number;
    company?: Company;
}

// ..\mowazaf\Data\General\Entities\Branch.cs
export interface Branch extends Record {
    id: number;
    name: string;
    companyId: number;
    company: Company;
}

// ..\mowazaf\Data\General\Entities\ClientType.cs
export enum ClientType {
    Web = 'Web',
    Mobile = 'Mobile',
}

export type ComplianceCountry = 'None' | 'UAE' | 'KSA';

// ..\mowazaf\Data\General\Entities\Company.cs
export interface Company extends Record {
    id: number;
    name: string;
    imageUrl?: string;
    organizationId?: number;
    organization?: Organization;
    email?: string;
    websiteLink?: string;
    address?: string;
    phoneNumber?: string;
    fax?: string;
    primaryColor?: string;
    countryId?: number;
    timeZone?: string;
    defaultStartTime?: string;
    defaultEndTime?: string;
    locales?: string;
    country?: Country;
    complianceCountry?: ComplianceCountry;
    companyRules?: CompanyRules;
    weekSchedule?: WeekSchedule;
    users?: User[];
    workplaces?: Workplace[];
    companyLeaves?: CompanyLeave[];
    molNumber?: string;
    bankRoutingCode?: string;
    wpsEmployeeThreshold?: number;
    gpssaAutoGenerationMode?: GpssaAutoGenerationMode;
    gpssaAutoGenerationDayOfMonth?: number;
    gpssaAutoGenerationLastRunAt?: string | null;
    employerNarrative?: string;
    commercialRegistrationNumber?: string;
    mudadEstablishmentNumber?: string;
    qiwaEntityId?: string;
}

export type GpssaAutoGenerationMode = 'Off' | 'AutoPreview' | 'AutoCommit';

// Seeding summary from backend
export interface SeedingSummary {
    message: string;
    rangeStart: string;
    rangeEnd: string;
    windowMonths: number;

    projects: number;
    workItems: number;
    shifts: number;
    breaks: number;
    scheduledShifts: number;
    leaves: number;
    claims: number;
    payslips: number;
    variablePays: number;
    adjustments: number;
    holidays: number;
}

// ..\mowazaf\Data\General\Entities\CompanyAttachmentType.cs
export interface CompanyAttachmentType extends Record {
    id: number;
    reminderDays: number;
    attachmentType: string;
    companyId: number;
    company?: Company;
    userAttachments?: UserAttachment[];
}

// ..\mowazaf\Data\General\Entities\CompanyCurrencyRate.cs
export interface CompanyCurrencyRate extends Record {
    id: number;
    baseCurrency: Currency;
    baseCurrencyId: number;
    foreignCurrency: Currency;
    foreignCurrencyId: number;
    company: Company;
    companyId: number;
    rate: number;
}

// ..\mowazaf\Data\General\Entities\CompanyCurrencyRateHIstory.cs
export interface CompanyCurrencyRateHistory extends Record {
    id: number;
    baseCurrency: Currency;
    baseCurrencyId: number;
    foreignCurrency: Currency;
    foreignCurrencyId: number;
    company: Company;
    companyId: number;
    rate: number;
    date: Date;
}

// ..\mowazaf\Data\General\Entities\CompanyLeave.cs
export interface CompanyLeave extends Record {
    id: number;
    eligibility: number;
    quotaUnit: QuotaUnit;
    quotaType: QuotaType;
    isCummulative: boolean;
    annuallyResets: boolean;
    includeNonWorkingrDays: boolean;
    leaveOverlapsOtherLeaves: boolean;
    leaveTypeId: number;
    attachmentRequired: boolean;
    leaveType?: LeaveType;
    companyId: number;
    company?: Company;
    countryId: number;
    country?: Country;
}

// ..\mowazaf\Data\General\Entities\CompanyNotificationType.cs
export interface CompanyNotificationType extends Record {
    id: number;
    emailsDisabled: boolean;
    mobilePushNotificationsDisabled: boolean;
    companyId: number;
    company: Company;
    notificationTypeId: number;
    notificationType: NotificationType;
}

// ..\mowazaf\Data\General\Entities\CompanyRules.cs
export interface CompanyRules extends Record {
    id: number;
    attendanceImageConfig: AttendanceImageConfig;
    faceRecognitionConfig: FaceRecognitionConfig;
    shiftFlowRuleType: ShiftFlowRuleType;
    timesheetCreationDay: number;
    hideClockingImagesFromTimesheet: boolean;
    disableAllNotifications: boolean;
    faceRecognitionEnabled: boolean;
    breakConfig: BreakConfig;
    triggerOvertimeVerificationOn: TriggerOvertimeVerificationOn;
    currency: Currency;
    currencyId: number;
    companyId: number;
    company: Company;
    bankId?: number;
    bank?: Bank;
}

// ..\mowazaf\Data\General\Entities\CompanyRules.cs
export enum AttendanceImageConfig {
    Required = 'Required',
    NotRequired = 'NotRequired',
}

// ..\mowazaf\Data\General\Entities\CompanyRules.cs
export enum FaceRecognitionConfig {
    Required = 'Required',
    NotRequired = 'NotRequired',
}

// ..\mowazaf\Data\General\Entities\CompanyRules.cs
export enum ShiftFlowRuleType {
    Default = 'Default',
    BoundByWorkingHours = 'BoundByWorkingHours',
}

// ..\mowazaf\Data\General\Entities\CompanyRules.cs
export enum TriggerOvertimeVerificationOn {
    ShiftWorkingHoursFullfiled = 'ShiftWorkingHoursFullfiled',
    ShiftEndTime = 'ShiftEndTime',
}

// ..\mowazaf\Data\General\Entities\CompanyRules.cs
// Which clock-out timestamp to record when a shift is auto-closed for unverified overtime.
export enum AutoClockOutTimestampMode {
    ScheduledShiftEnd = 'ScheduledShiftEnd',
    ActualTime = 'ActualTime',
}

// ..\mowazaf\Data\General\Entities\CompanyRules.cs
// Which salary base a monetary payroll calculation is computed from. Serialized as the enum's
// STRING NAME, like every other enum in this API: Program.cs registers a global Newtonsoft
// StringEnumConverter, which is why AttendanceImageConfig and friends above are string-valued too.
export enum PayrollSalaryBaseMode {
    /** The component flagged as the basic salary, falling back to the sole component, then gross. */
    Auto = 'Auto',
    /** ONLY the component flagged as the basic salary. Yields zero when none is configured. */
    BasicOnly = 'BasicOnly',
    /** Always the full gross salary. */
    Gross = 'Gross',
}

// ..\mowazaf\Data\Payroll\Enums\OvertimeNoManagerRouting.cs
// CO-348 — where an overtime approval request goes when the employee has NO able manager.
// Serialized as the enum's STRING NAME (global Newtonsoft StringEnumConverter), like every other
// enum in this API.
export enum OvertimeNoManagerRouting {
    /** The request is raised to everyone holding the HR role. The server-side default. */
    RouteToHr = 'RouteToHr',
    /** The overtime is stamped approved on the spot, with nobody reviewing it. */
    AutoApprove = 'AutoApprove',
}

/**
 * Documented server-side defaults for the payroll calculation-basis fields. Every one of the
 * fields is nullable on the wire, and null means "not configured yet, use the default". Read them
 * through these constants with `??` so a null can never be rendered as an empty control. Never use
 * `||` here: the multipliers and the hours divisor are numbers, and the base modes are string enums
 * whose 'Auto' member is a perfectly valid configured value, so a truthiness test would be wrong.
 */
export const PAYROLL_BASIS_DEFAULTS = {
    useAttendanceOvertime: true,
    overtimeBaseMode: PayrollSalaryBaseMode.Auto,
    eosBaseMode: PayrollSalaryBaseMode.Auto,
    overtimeRegularMultiplier: 1.25,
    overtimePremiumMultiplier: 1.5,
    hoursPerMonth: 208,
    // CO-348. Matches PayrollSettings.ResolveNoManagerRouting() — null resolves to RouteToHr, the
    // safe member, because auto-approving overtime nobody asked for would pay money by default.
    noManagerRouting: OvertimeNoManagerRouting.RouteToHr,
} as const;

/** Statutory floors the server also enforces. Client-side validation mirrors them exactly. */
export const PAYROLL_OVERTIME_REGULAR_MIN = 1.25;
export const PAYROLL_OVERTIME_PREMIUM_MIN = 1.5;

// ── Hourly pay + per-shift approval (docs/hourly-pay-spec.md §3) ─────────────────────────────
// Every enum below travels as its STRING NAME (global Newtonsoft StringEnumConverter).

/** §3.1 — `User.PayBasis`. NOT `EmploymentType`; the two are independent. */
export type PayBasis = 'Salaried' | 'Hourly';

/** §3.3 — how each item's paid minutes are rounded to `hourlyRoundingMinutes`. */
export type HourlyRoundingMode = 'Nearest' | 'Down' | 'Up';

/** §3.3 — `HourlyWeekStartDay` is a .NET `DayOfWeek`. */
export type WeekDayName =
    | 'Sunday'
    | 'Monday'
    | 'Tuesday'
    | 'Wednesday'
    | 'Thursday'
    | 'Friday'
    | 'Saturday';

export const WEEK_DAY_NAMES: WeekDayName[] = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];

/** §3.3 — the only rounding steps the server accepts. 0 = no rounding. */
export const HOURLY_ROUNDING_STEPS = [0, 5, 10, 15, 30] as const;

/**
 * §3.3 documented defaults. Every hourly field is NULLABLE on the wire and null means "server
 * default", so read through these with `??`, never `||` (0 minutes and 1.0 are real values).
 * `maxPaidMinutesPerItem` / `thresholdMinutes` default to null = "no cap" / "no threshold".
 */
export const HOURLY_PAY_DEFAULTS = {
    enabled: false,
    roundingMinutes: 0,
    roundingMode: 'Nearest' as HourlyRoundingMode,
    minPaidMinutesPerItem: 0,
    maxPaidMinutesPerItem: null as number | null,
    restDayMultiplier: 1,
    publicHolidayMultiplier: 1,
    thresholdMinutes: null as number | null,
    overThresholdMultiplier: 1,
    autoApproveEnabled: false,
    autoApproveToleranceMinutes: 10,
    weekStartDay: 'Monday' as WeekDayName,
};

/**
 * The hourly-pay block of CompanyRules, shared by the input and the view. Nullable everywhere for
 * the partial-editor reason documented on CompanyRulesInput: every CompanyRules editor POSTs the
 * whole DTO, so a non-nullable member here would let any OTHER settings page reset it.
 */
export interface CompanyRulesHourlyPayFields {
    hourlyPayEnabled?: boolean | null;
    /** Null = "create/use the auto 'Hourly Pay' type on enable". */
    hourlyPayVariablePayTypeId?: number | null;
    hourlyRoundingMinutes?: number | null;
    hourlyRoundingMode?: HourlyRoundingMode | null;
    hourlyMinPaidMinutesPerItem?: number | null;
    /** Null = no cap. */
    hourlyMaxPaidMinutesPerItem?: number | null;
    hourlyRestDayMultiplier?: number | null;
    hourlyPublicHolidayMultiplier?: number | null;
    /** Null = no threshold. */
    hourlyThresholdMinutes?: number | null;
    hourlyOverThresholdMultiplier?: number | null;
    shiftAutoApproveEnabled?: boolean | null;
    shiftAutoApproveToleranceMinutes?: number | null;
    hourlyWeekStartDay?: WeekDayName | null;
}

/**
 * Per-request instructions, INPUT only, never stored. A partial-update DTO cannot say "write null"
 * by sending null (null = field not sent), so the two "no cap" fields need an explicit clear flag,
 * the same convention as `clearPayrollRequireOvertimeApproval`.
 * ⚠️ NOT named in docs/hourly-pay-spec.md §3.3 — the web lane's assumption, to be confirmed.
 */
export interface CompanyRulesHourlyPayClearFlags {
    clearHourlyMaxPaidMinutesPerItem?: boolean | null;
    clearHourlyThresholdMinutes?: boolean | null;
}

// ..\mowazaf\Data\General\Entities\CompanyRules.cs
export enum BreakConfig {
    Required = 'Required',
    NotRequired = 'NotRequired',
}

// ..\mowazaf\Data\General\Entities\Country.cs
export interface Country extends Record {
    id: number;
    name: string;
    iso: string;
    nicename: string;
    iso3?: string;
    numCode?: number;
    phoneCode: number;
    isGcc: boolean;
    companies?: Company[];
}

// ..\mowazaf\Data\General\Entities\Currency.cs
export interface Currency extends Record {
    id: number;
    name?: string;
    currencyCode?: string;
    country: Country;
    countryId?: number;
    currencySymbol?: string;
}

// ..\mowazaf\Data\General\Entities\Department.cs
export interface Department extends Record {
    id: number;
    name: string;
    companyId: number;
    company?: Company;
    parentDepartmentId?: number | null;
    parentDepartment?: Department | null;
    parentDepartmentName?: string | null;
    subDepartmentCount?: number;
    employeeCount?: number;
}

// ..\mowazaf\Data\General\Entities\EmailTask.cs
export interface EmailTask {
    id: number;
    to: string;
    createdOn: Date;
    modelJson: string;
    status: EmailTaskStatus;
    template: EmailTemplate;
    subscriptionId?: number;
}

// ..\mowazaf\Data\General\Entities\EmailTask.cs
export enum EmailTaskStatus {
    Pending = 'Pending',
    Sent = 'Sent',
    Error = 'Error',
}

// ..\mowazaf\Data\General\Entities\EmailTask.cs
export enum EmailTemplate {
    VerifyEmail = 'VerifyEmail',
    ResetPassword = 'ResetPassword',
}

// ..\mowazaf\Data\General\Entities\Group.cs
export interface Group extends Record {
    id: number;
    name: string;
    companyId: number;
    company: Company;
}

// ..\mowazaf\Data\General\Entities\GroupLeave.cs
export interface GroupLeave extends Record {
    id: number;
    eligibility: number;
    quotaUnit: QuotaUnit;
    quotaType: QuotaType;
    isCummulative: boolean;
    annuallyResets: boolean;
    includeNonWorkingrDays: boolean;
    leaveOverlapsOtherLeaves: boolean;
    leaveTypeId: number;
    attachmentRequired: boolean;
    leaveType?: LeaveType;
    groupId: number;
    group?: Group;
}

// ..\mowazaf\Data\General\Entities\GroupRules.cs
export interface GroupRules extends Record {
    id: number;
    attendanceImageConfig: AttendanceImageConfig;
    faceRecognitionConfig: FaceRecognitionConfig;
    shiftFlowRuleType: ShiftFlowRuleType;
    timesheetCreationDay: number;
    hideClockingImagesFromTimesheet: boolean;
    disableAllNotifications: boolean;
    faceRecognitionEnabled: boolean;
    breakConfig: BreakConfig;
    triggerOvertimeVerificationOn: TriggerOvertimeVerificationOn;
    currency: Currency;
    currencyId: number;
    groupId: number;
    group: Group;
    bankId?: number;
    bank?: Bank;
}

// ..\mowazaf\Data\General\Entities\Holiday.cs
export interface Holiday extends Record {
    id: number;
    companyId: number;
    countryId: number;
    name: string;
    description?: string;
    date: Date;
    holidayType?: string;
    locations?: string;
    recurrence?: string;
    isMandatory?: boolean;
}

// ..\mowazaf\Data\General\Entities\IRecord.cs
export interface IRecord {
    createdOn: Date;
    updatedOn?: Date;
    createdById?: string;
    createdBy?: User;
    updatedById?: string;
    updatedBy?: User;
    isArchived: boolean;
}

// ..\mowazaf\Data\General\Entities\License.cs
export interface License extends Record {
    id: number;
    planId?: number;
    companyId: number;
    maxUsers: number;
    price: number;
    expiresOn: Date;
    plan?: SubscriptionPlan;
    company?: Company;
}

// ..\mowazaf\Data\General\Entities\Log.cs
export interface Log extends Record {
    id: number;
    logType: LogType;
    logSource: LogSource;
    message?: string;
    stackTrace?: string;
    source?: string;
    path?: string;
    protocol?: string;
    method?: string;
    clientIpAddress?: string;
    clientUserAgent?: string;
}

// ..\mowazaf\Data\General\Entities\Log.cs
export enum LogType {
    Info = 'Info',
    Warning = 'Warning',
    Error = 'Error',
    BugReport = 'BugReport',
}

// ..\mowazaf\Data\General\Entities\Log.cs
export enum LogSource {
    Request = 'Request',
    FCM = 'FCM',
    RefreshToken = 'RefreshToken',
    Login = 'Login',
    LeaveBalanceUpdate = 'LeaveBalanceUpdate',
    Currency = 'Currency',
    GenerateMonthlyPayroll = 'GenerateMonthlyPayroll',
}

// ..\mowazaf\Data\General\Entities\LogAttachment.cs
export interface LogAttachment extends Record {
    id: number;
    name: string;
    extention: string;
    byteLength: number;
    path: string;
    logId: number;
    log: Log;
}

// ..\mowazaf\Data\General\Entities\Notification.cs
export interface Notification extends Record {
    id: number;
    title?: string;
    titleParams?: string;
    body?: string;
    bodyParams?: string;
    emailTemplate?: string;
    dataJson?: string;
    notificationUrl?: string;
    imageUrl?: string;
    isRead: boolean;
    notificationTypeId?: number;
    notificationType?: NotificationType;
    fromId?: string;
    from?: User;
    toId?: string;
    ccIds?: string;
    excludedToAndCcIds?: string;
    actionSource?: string;
    actionEntityId?: string;
}

// ..\mowazaf\Data\General\View Models\NotificationView.cs
export interface NotificationView {
    id: number;
    title?: string;
    titleParams?: string;
    body?: string;
    bodyParams?: string;
    notificationUrl?: string;
    imageUrl?: string;
    isRead: boolean;
    createdOn: string;
    notificationTypeId?: number;
    notificationType?: NotificationTypeView;
    actionSource?: string;
    actionEntityId?: string;
    dataJson?: string;
}

// ..\mowazaf\Data\General\View Models\NotificationView.cs
export interface NotificationTypeView {
    id: number;
    name?: string;
}

// ..\mowazaf\Data\General\Entities\NotificationImage.cs
export interface NotificationImage extends Record {
    id: number;
    name: string;
    extention: string;
    byteLength: number;
    path: string;
    notificationId: number;
    notification: Notification;
}

// ..\mowazaf\Data\General\Entities\NotificationType.cs
export interface NotificationType {
    id: number;
    name: string;
    defaultTitle?: string;
    defaultBody?: string;
}

// ..\mowazaf\Data\General\Entities\Organization.cs
export interface Organization extends Record {
    id: number;
    name: string;
    companies: Company[];
}

// ..\mowazaf\Data\General\Entities\PerformanceTimeLog.cs
export interface PerformanceTimeLog extends Record {
    id: number;
    action: string;
    startLineNumber: number;
    stopLineNumber: number;
    timeTaken: string;
}

// ..\mowazaf\Data\General\Entities\Priority.cs
export interface Priority {
    id: number;
    name: string;
}

// ..\mowazaf\Data\General\Entities\Record.cs
export interface Record extends IRecord {
    createdOn: Date;
    updatedOn?: Date;
    createdById?: string;
    createdBy?: User;
    updatedById?: string;
    updatedBy?: User;
    isArchived: boolean;
}

// ..\mowazaf\Data\General\Entities\ReportSchedulingConfig.cs
export interface ReportSchedulingConfig extends Record {
    id: number;
    reportName: string;
    companyId: number;
    companyName: string;
    companyTimezone: string;
    usersTo: string;
    usersCc: string;
    cronExpression: string;
    daysBeforeDate: number;
    daysAfterDate: number;
    parameters: string;
}

// ..\mowazaf\Data\General\Entities\Request.cs
export interface Request extends Record {
    id: number;
    closedDate: Date;
    expectedClosedDate: Date;
    title: string;
    description: string;
    requestedById: string;
    requestStatusId: number;
    requestPriorityId?: number;
    requestStatus: RequestStatus;
    requestedBy: User;
    requestPriority: Priority;
}

// ..\mowazaf\Data\General\Entities\RequestStatus.cs
export interface RequestStatus {
    id: number;
    name: string;
}

// ..\mowazaf\Data\General\Entities\Role.cs
export interface Role {
    createdOn: Date;
    updatedOn?: Date;
    createdById?: string;
    updatedById?: string;
    isArchived: boolean;
    haveAuthorityOverRoles?: string;
    createdBy?: User;
    updatedBy?: User;
}

// ..\mowazaf\Data\General\Entities\Role.cs
export enum Roles {
    Clocking = 'Clocking',
    Hr = 'Hr',
    Chief = 'Chief',
    Admin = 'Admin',
    Operator = 'Operator',
    Manager = 'Manager',
    AssetManagement = 'AssetManagement',
}

// ..\mowazaf\Data\General\Entities\RoleLicense.cs
export interface RoleLicense extends Record {
    id: number;
    roleId?: string;
    licenseId: number;
    maxUsers: number;
    role?: Role;
    license?: License;
}

// ..\mowazaf\Data\General\Entities\SubscriptionPlan.cs
export interface SubscriptionPlan extends Record {
    id: number;
    name: string;
    monthlyPricePerUser?: number;
    yearlyPricePerUser?: number;
    duration?: string;
}

// ..\mowazaf\Data\General\Entities\TrialCompanyData.cs
export interface TrialCompanyData extends Record {
    id: number;
    industry: string;
    size: string;
    days: number;
    reference: string;
    companyId: number;
    company: Company;
}

// ..\mowazaf\Data\General\Entities\User.cs
export interface User extends IRecord {
    title?: string;
    employeeId?: string;
    branchId?: number;
    branch?: Branch;
    departmentId?: number;
    department?: Department;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    gender?: Gender;
    linkedInUrl?: string;
    birthDate?: Date;
    fcmToken?: string;
    imageUrl?: string;
    timeZone?: string;
    locale: string;
    fiscalYearStartingMonth: number;
    currentStatus: UserCurrentStatus;
    currentCustomStatusText?: string;
    predifinedImagesValidForFaceRecognition: boolean;
    companyId?: number;
    company?: Company;
    countryId?: number;
    country?: Country;
    hireDate: Date;
    employeeUniqueId?: string;
    ibanNumber?: string;
    vehicleId?: string;
    userRules?: UserRules;
    shifts?: Shift[];
    workplaces?: Workplace[];
    weekSchedule?: WeekSchedule;
    timesheet?: Timesheet;
    createdOn: Date;
    updatedOn?: Date;
    createdById?: string;
    updatedById?: string;
    isArchived: boolean;
    createdBy?: User;
    updatedBy?: User;
}

// ..\mowazaf\Data\General\Entities\User.cs
export enum Gender {
    Female = 'Female',
    Male = 'Male',
}

// ..\mowazaf\Data\General\Entities\User.cs
export enum MaritalStatus {
    Single = 'Single',
    Engaged = 'Engaged',
    Married = 'Married',
    Divorced = 'Divorced',
    Widowed = 'Widowed',
}

// ..\mowazaf\Data\General\Entities\UserAttachment.cs
export interface UserAttachment extends Attachment {
    reminder: number;
    expiryDate?: Date;
    userId: string;
    user?: User;
    companyAttachmentTypeId?: number;
    companyAttachmentType?: CompanyAttachmentType;
}

// ..\mowazaf\Data\General\Entities\UserAttachmentAccess.cs
export interface UserAttachmentAccess extends Record {
    id: number;
    userId: string;
    user?: User;
    userAttachmentId?: number;
    userAttachment?: UserAttachment;
    userClaimAttachmentId?: number;
    userClaimAttachment?: UserClaimAttachment;
    accessType: AccessType;
}

// ..\mowazaf\Data\General\Entities\UserAttachmentAccess.cs
export enum AccessType {
    Owner = 'Owner',
    Contributer = 'Contributer',
    Viewer = 'Viewer',
}

// ..\mowazaf\Data\General\Entities\UserCurrentStatus.cs
export enum UserCurrentStatus {
    Unknown = 'Unknown',
    Available = 'Available',
    Unavailable = 'Unavailable',
    Break = 'Break',
    Leave = 'Leave',
    Holiday = 'Holiday',
    HalfDayLeave = 'HalfDayLeave',
}

// ..\mowazaf\Data\General\Entities\UserGroup.cs
export interface UserGroup extends Record {
    id: number;
    groupId: number;
    group: Group;
    userId: string;
    user: User;
}

// ..\mowazaf\Data\General\Entities\UserRefreshToken.cs
export interface UserRefreshToken {
    id: number;
    token: string;
    expires?: Date;
    isExpired: boolean;
    created: Date;
    createdByIp?: string;
    revoked?: Date;
    revokedByIp?: string;
    replacedByToken?: string;
    isActive: boolean;
    userId: string;
    user: User;
}

// ..\mowazaf\Data\General\Entities\UserRequest.cs
export interface UserRequest {
    id: number;
    userId: string;
    requestId: number;
    request: Request;
    user: User;
}

// ..\mowazaf\Data\General\Entities\UserRules.cs
export interface UserRules extends Record {
    id: number;
    attendanceImageConfig: UserAttendanceImageConfig;
    faceRecognitionConfig: UserFaceRecognitionConfig;
    workplaceConfig: WorkplaceConfig;
    userId?: string;
    user?: User;
    userCurrency: UserCurrency;
    currencyId?: number;
    currency?: Currency;
    userBank: UserBank;
    bankId?: number;
    bank?: Bank;
}

// ..\mowazaf\Data\General\Entities\UserRules.cs
export enum UserAttendanceImageConfig {
    Required = 'Required',
    NotRequired = 'NotRequired',
    InheritedFromCompany = 'InheritedFromCompany',
}

// ..\mowazaf\Data\General\Entities\UserRules.cs
export enum UserFaceRecognitionConfig {
    InheritedFromCompany = 'InheritedFromCompany',
    Required = 'Required',
    NotRequired = 'NotRequired',
}

// ..\mowazaf\Data\General\Entities\UserRules.cs
export enum UserLeaveWithoutScheduleConfig {
    InheritedFromCompany = 'InheritedFromCompany',
    Allowed = 'Allowed',
    NotAllowed = 'NotAllowed',
}

// ..\mowazaf\Data\General\Entities\UserRules.cs
export enum UserShiftSwapConfig {
    InheritedFromCompany = 'InheritedFromCompany',
    Enabled = 'Enabled',
    Disabled = 'Disabled',
}

// ..\mowazaf\Data\General\Entities\UserRules.cs
export enum UserShiftSwapAutoApproveConfig {
    InheritedFromCompany = 'InheritedFromCompany',
    Enabled = 'Enabled',
    Disabled = 'Disabled',
}

// ..\mowazaf\Data\General\Entities\UserRules.cs
export enum UserCurrency {
    InheritedFromCompany = 'InheritedFromCompany',
    CustomCurrency = 'CustomCurrency',
}

// ..\mowazaf\Data\General\Entities\UserRules.cs
export enum UserBank {
    InheritedFromCompany = 'InheritedFromCompany',
    CustomBank = 'CustomBank',
}

// ..\mowazaf\Data\General\Entities\UserManager.cs
export interface UserManager extends Record {
    id: number;
    userId?: string;
    user?: User;
    managerId?: string;
    manager?: User;
}

// CO-227 — ActivateTrialCompanyInput removed; the server input model is deleted.

// ..\mowazaf\Data\General\Input Models\AttachmentDownloadInput.cs
export interface AttachmentInput {
    attachmentId: number;
}

// ..\mowazaf\Data\General\Input Models\BranchInput.cs
export interface BranchInput {
    id: number;
    name: string;
}

// ..\mowazaf\Data\General\Input Models\ChangePasswordInput.cs
export interface ChangePasswordInput {
    oldPassword: string;
    newPassword: string;
    newPasswordVerify: string;
}

// ..\mowazaf\Data\General\Input Models\CompanyAttachmentTypeInput.cs
export interface CompanyAttachmentTypeInput {
    attachmentType: string;
    reminderDays: number;
}

// ..\mowazaf\Data\General\Input Models\CompanyCurrencyRateInput.cs
export interface CompanyCurrencyRateInput {
    baseCurrencyId: number;
    foreignCurrencyId: number;
    companyId: number;
    rate: number;
}

// ..\mowazaf\Data\General\Input Models\CompanyInput.cs
export interface CompanyInput {
    name: string;
    image?: File | null;
    planId: number;
    rolesMaxUsers: any;
    countryId: number;
    address?: string;
    phoneNumber?: string;
    email?: string;
    websiteLink?: string;
    organizationId: number;
    primaryColor?: string;
    timeZone: string;
    locales?: string;
    adminFirstName?: string;
    adminLastName?: string;
    adminEmail?: string;
    employerNarrative?: string;
    molNumber?: string;
    bankRoutingCode?: string;
    wpsEmployeeThreshold?: number;
    commercialRegistrationNumber?: string;
    mudadEstablishmentNumber?: string;
    qiwaEntityId?: string;
    gpssaAutoGenerationMode?: GpssaAutoGenerationMode;
    gpssaAutoGenerationDayOfMonth?: number;
}

// ..\mowazaf\Data\General\Input Models\CompanyRulesInput.cs
// Hourly pay (spec §3.3) rides the same DTO — see CompanyRulesHourlyPayFields.
export interface CompanyRulesInput extends CompanyRulesHourlyPayFields, CompanyRulesHourlyPayClearFlags {
    id: number;
    attendanceImageConfig: AttendanceImageConfig;
    faceRecognitionConfig: FaceRecognitionConfig;
    shiftFlowRuleType: ShiftFlowRuleType;
    timesheetCreationDay: number;
    hideClockingImagesFromTimesheet: boolean;
    disableAllNotifications: boolean;
    faceRecognitionEnabled: boolean;
    triggerOvertimeVerificationOn: TriggerOvertimeVerificationOn;
    breakConfig: BreakConfig;

    // Overtime verification & auto clock-out timing (nullable so partial editors don't clobber).
    autoClockOutOnUnverifiedOvertime?: boolean | null;
    overtimeVerificationGraceMinutes?: number | null;
    autoClockOutAfterMinutes?: number | null;
    overtimeRecheckIntervalHours?: number | null;
    autoClockOutTimestampMode?: AutoClockOutTimestampMode | null;

    // CO-583 — overtime REQUEST tolerance. Governs only whether an overtime approval REQUEST is
    // raised; never overtime hours or pay. Nullable for the same partial-editor reason as the
    // timers above.
    overtimeRequestEarlyClockInGraceMinutes?: number | null;
    overtimeRequestLateClockOutGraceMinutes?: number | null;

    // CO-240 — Engagement & Performance job enablement (per company, HR-managed).
    // Nullable on purpose: the Engagement Jobs tab is a partial editor and every other CompanyRules
    // editor POSTs the whole DTO, so a non-nullable field would silently switch these jobs off.
    // `enableAINudges` is the master AI-nudge switch and writes Company.EnableAINudges, not a
    // CompanyRules column — it rides this DTO so one GET/POST covers the whole surface.
    enableRecognitionGapReminder?: boolean | null;
    enableCheckInReminder?: boolean | null;
    enableQ12Nudge?: boolean | null;
    enableAINudges?: boolean | null;

    // CO-354 — asset replenishment notification (type 112) enablement. Nullable for the same
    // partial-editor reason as the CO-240 flags: null means "not sent, leave stored value alone",
    // never "on". The stored default is false (OFF).
    enableAssetReplenishmentNotification?: boolean | null;

    // Payroll calculation basis (per company, HR-managed). Nullable on purpose: every
    // CompanyRules editor POSTs the whole DTO, and null means "server default", so a
    // non-nullable field here would let a partial editor reset a tenant's payroll basis.
    payrollUseAttendanceOvertime?: boolean | null;
    // CO-280 / CO-304 — pay only approved overtime hours. TRISTATE on the wire:
    //   true  => explicit opt-in (server preflights + requires an acknowledgement, see below)
    //   false => explicit opt-out
    //   null / omitted => field not sent, the stored value is left alone.
    // "Not configured" resolves to OFF (PayrollSettings.RequiresOvertimeApprovalOn).
    payrollRequireOvertimeApproval?: boolean | null;
    // CO-304 — resets payrollRequireOvertimeApproval back to NULL ("not configured"). A
    // partial-update DTO cannot express "write null" by sending null, hence the separate flag.
    // Takes precedence over payrollRequireOvertimeApproval when both are sent.
    clearPayrollRequireOvertimeApproval?: boolean | null;
    // CO-304 — two-step arming. The FIRST attempt to set payrollRequireOvertimeApproval = true is
    // refused with the measured impact in the error message; the caller re-sends with this true
    // once a human has read it. NEVER send it pre-emptively — reading the impact is the point.
    acknowledgeOvertimeApprovalGateImpact?: boolean | null;
    // CO-348 — where an overtime approval request goes when the employee has no able manager.
    // TRISTATE, same shape as payrollRequireOvertimeApproval above: null / omitted leaves the
    // stored value alone and resolves to RouteToHr. Only meaningful while approval is required.
    payrollNoManagerOvertimeApproval?: OvertimeNoManagerRouting | null;
    // CO-348 — resets payrollNoManagerOvertimeApproval back to NULL ("not configured"). Takes
    // precedence over payrollNoManagerOvertimeApproval when both are sent.
    clearPayrollNoManagerOvertimeApproval?: boolean | null;
    payrollOvertimeBaseMode?: PayrollSalaryBaseMode | null;
    payrollEosBaseMode?: PayrollSalaryBaseMode | null;
    payrollOvertimeRegularMultiplier?: number | null;
    payrollOvertimePremiumMultiplier?: number | null;
    payrollHoursPerMonth?: number | null;

    // Attendance notifications timing (per-company)
    scheduledShiftClockInReminderOffsetMinutes: number;
    scheduledShiftClockOutReminderOffsetMinutes: number;
    scheduledShiftClockInReminderBeforeMinutes?: number | null;
    scheduledShiftClockOutReminderBeforeMinutes?: number | null;
    scheduledShiftEarlyClockInWindowMinutes: number;
    breakInReminderMinutesAfterClockIn?: number | null;
    breakOutReminderMinutesAfterBreakIn: number;

    // Attendance thresholds (per-company)
    scheduledShiftAbsentThresholdPercent: number;
    scheduledShiftHalfDayThresholdPercent: number;
    scheduledShiftLateClockInThresholdMinutes: number;
    scheduledShiftEarlyClockOutThresholdMinutes: number;
    companyId: number;

    // Wialon GPS Vehicle Tracking
    wialonEnabled?: boolean | null;
    wialonToken?: string | null;
    clearWialonToken?: boolean | null;

    // AI Configuration
    aiFeaturesEnabled?: boolean | null;
    azureOpenAIEndpoint?: string | null;
    azureOpenAIApiKey?: string | null;
    azureOpenAIChatDeployment?: string | null;
    clearAIKey?: boolean | null;

    // Leave-without-schedule + shift-swap policies (nullable so partial editors don't clobber).
    // leaveWithoutScheduleWorkingDays is a 7-bit mask over JS getDay() (bit 0 = Sunday … bit 6 = Saturday).
    allowLeaveWithoutSchedule?: boolean | null;
    leaveWithoutScheduleWorkingDays?: number | null;
    shiftSwapEnabled?: boolean | null;
    shiftSwapApprovalLimitedToManager?: boolean | null;
    shiftSwapAutoApproveOnPeerAccept?: boolean | null;
    shiftSwapMinAdvanceNoticeHours?: number | null;
    shiftSwapMaxHorizonDays?: number | null;
    shiftSwapMaxActiveRequestsPerUser?: number | null;

    // Geofence / out-of-bounds clocking policy (nullable so partial editors don't clobber).
    workplaceConfig?: WorkplaceConfig | null;
    outOfBoundsPolicy?: OutOfBoundsPolicy | null;
    noLocationPolicy?: NoLocationPolicy | null;
    workplaceBufferMeters?: number | null;
    notifyManagerOnOutOfBounds?: boolean | null;
    notifyHrOnOutOfBounds?: boolean | null;
    outOfBoundsApprovalLimitedToManager?: boolean | null;
    // Company-configurable quick-fill reason chips offered when clocking outside the allowed area.
    // null = leave untouched; empty = clear to the shared default. Server caps at 12 items / 40 chars.
    outOfBoundsReasonPresets?: string[] | null;
}

// ..\mowazaf\Data\General\Input Models\DepartmentInput.cs
export interface DepartmentInput {
    id: number;
    name: string;
    parentDepartmentId?: number | null;
}

export interface BatchMigrationSetupResult {
    branches: Branch[];
    departments: Department[];
}

// ..\mowazaf\Data\General\Input Models\EditBankInput.cs
export interface EditBankInput {
    name: string;
    routingNumber: string;
}

// CO-227 — ExtendTrialInput removed; the server input model is deleted.

// ..\mowazaf\Data\General\Input Models\GroupInput.cs
export interface GroupInput {
    id: number;
    name: string;
}

// ..\mowazaf\Data\General\Input Models\GroupRulesInput.cs
export interface GroupRulesInput {
    id: number;
    attendanceImageConfig: AttendanceImageConfig;
    faceRecognitionConfig: FaceRecognitionConfig;
    shiftFlowRuleType: ShiftFlowRuleType;
    timesheetCreationDay: number;
    hideClockingImagesFromTimesheet: boolean;
    disableAllNotifications: boolean;
    faceRecognitionEnabled: boolean;
    triggerOvertimeVerificationOn: TriggerOvertimeVerificationOn;
    breakConfig: BreakConfig;
    groupId: number;
}

// ..\mowazaf\Data\General\Input Models\HolidayConfigInput.cs
export interface HolidayConfigInput {
    id: number;
    enabled: boolean;
    date: string;
    holidayId: number;
    holiday?: Holiday;
    companyId: number;
    company?: Company;
    countryId: number;
    country?: Country;
}

// ..\mowazaf\Data\General\Input Models\HolidayInput.cs
export interface HolidayInput {
    id: number;
    name: string;
    defaultDate: string;
    description: string;
}

// ..\mowazaf\Data\General\Input Models\HolidaysConfigInput.cs
export interface HolidaysConfigInput {
    companyId: number;
    holidayConfigInputList: HolidayConfigInput[];
}

// ..\mowazaf\Data\General\Input Models\HolidaysInput.cs
export interface HolidaysInput {
    companyId: number;
    holidayInputList: HolidayInput[];
}

// ..\mowazaf\Data\General\Input Models\ImageInput.cs
export interface ImageInput {
    image: File | null;
}

// ..\mowazaf\Data\General\Input Models\InputRecoveryTemplateModel.cs
export interface InputRecoveryTemplateModel {
    basUrl: string;
    token: string;
    email: string;
}

// ..\mowazaf\Data\General\Input Models\LogAttacmentInput.cs
export interface LogAttachmentInput {
    logId: number;
    attachments: File[];
}

// ..\mowazaf\Data\General\Input Models\LogDetailsInput.cs
export interface LogDetailsInput {
    message: string;
    logType: LogType;
    logSource: LogSource;
    clientUserAgent: string;
    attachments: File[];
}

// ..\mowazaf\Data\General\Input Models\LoginInput.cs
export interface LoginInput {
    email: string;
    password: string;
    fcmToken: string;
}

// ..\mowazaf\Data\General\Input Models\LogoutInput.cs
export interface LogoutInput {
    removeFcmToken: boolean;
}

// ..\mowazaf\Data\General\Input Models\NotificationInput.cs
export interface NotificationInput {
    title: string;
    titleParams: string;
    body: string;
    bodyParams: string;
    emailTemplate: string;
    linkId: string;
    screen: string;
    request: string;
    userId: string;
    from: string;
    to: string[];
    cc: string[];
    excludeToAndCc: string[];
    notificationTypeId: number;
    shiftId: number;
}

// ..\mowazaf\Data\General\Input Models\OnDemandNotificationInput.cs
export interface OnDemandNotificationInput {
    title: string;
    body: string;
    userId?: string;
    to: string[];
    cc?: string[];
    image?: File | null;
    sendEmail: boolean;
}

// ..\mowazaf\Data\General\Input Models\OrganizationInput.cs
export interface OrganizationInput {
    name: string;
}

// ..\mowazaf\Data\General\Input Models\PaginationFilter.cs
export interface PaginationFilter {
    pageNumber: number;
    pageSize: number;
}

// ..\mowazaf\Data\General\Input Models\PasswordRecoveryInput.cs
export interface PasswordRecoveryInput {
    email: string;
}

// ..\mowazaf\Data\General\Input Models\PasswordResetInput.cs
export interface PasswordResetInput extends PasswordRecoveryInput {
    token: string;
    newPassword: string;
    newPasswordVerify: string;
}

// ..\mowazaf\Data\General\Input Models\ProfileImageInput.cs
export interface ProfileImageInput {
    image?: File | null;
}

// ..\mowazaf\Data\General\Input Models\ProfileInput.cs
export interface ProfileInput {
    image?: File | null;
    employeeId?: string;
    title?: string;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    userName: string;
    linkedInUrl?: string;
    birthDate?: Date;
    role: string;
    // CO-248: these mirror a C# DTO whose members are now NULLABLE on purpose — omitting one means
    // "leave the stored value alone", where before it bound to 0 / default(Gender) / 0001-01-01 and
    // was written over the employee's record. Declaring them required here made the mirror lie and
    // pushed callers into sending a placeholder (the exact value that used to do the damage).
    // companyId is accepted and IGNORED by the server: tenant membership is not editable through a
    // profile save.
    countryId?: number;
    companyId?: number;
    branchId?: number;
    departmentId?: number;
    phoneNumber: string;
    timeZone?: string;
    fiscalYearStartingMonth?: number;
    // Omitted/undefined = keep the current reporting line; an EMPTY ARRAY that is actually sent =
    // remove every manager.
    managerIds?: string[];
    managerNames?: string[];
    hireDate?: Date;
    probationEndDate?: Date;
    gender?: Gender;
    maritalStatus?: MaritalStatus;
    employeeUniqueId?: string;
    ibanNumber?: string;
    vehicleId?: string;
    employmentType?: string;
    molListNumber?: string;
    fileNumber?: string;
    uidNumber?: string;
    visaDesignation?: string;
    bankAccountNumber?: string;
    address?: string;
    nationalityId?: number;
    emirate?: string;
    passportNumber?: string;
    passportExpiryDate?: Date;
    emiratesIdNumber?: string;
    emiratesIdExpiryDate?: Date;
    labourCardNumber?: string;
    labourCardExpiryDate?: Date;
    visaIdNumber?: string;
    visaExpiryDate?: Date;
    iqamaNumber?: string;
    iqamaExpiryDate?: string;
    saudiNationalIdNumber?: string;
    qiwaContractNumber?: string;
    muqeemIdNumber?: string;
    visaCategory?: string;
    personalEmail?: string;
}

// ..\mowazaf\Data\General\Input Models\RemoveUserFromGr.cs
export interface RemoveUsersFromGroup {
    userId: string[];
    groupId: number;
}

// ..\mowazaf\Data\General\Input Models\ReportBugTemplate.cs
export interface ReportBugTemplate {
    message: string;
    username: string;
    email: string;
    attachments: LogAttachment[];
}

// ..\mowazaf\Data\General\Input Models\ReportCreationTemplateModel.cs
export interface ReportCreationTemplateModel {
    baseUrl: string;
    startDate: string;
    endDate: string;
    reportName: string;
    companyName: string;
}

// ..\mowazaf\Data\General\Input Models\ReportSchedulingConfigInput.cs
export interface ReportSchedulingConfigInput {
    reportName: string;
    companyId: number;
    usersTo: string;
    usersCc: string;
    cronExpression: string;
    daysBeforeDate: number;
    daysAfterDate: number;
    parameters: string;
}

// ..\mowazaf\Data\General\Input Models\RequestInput.cs
export interface RequestInput {
    title: string;
    description: string;
    assignedUsers: string[];
    requestedById: string;
    expectedClosedDate: Date;
    statusId: number;
    priorityId: number;
}

// ..\mowazaf\Data\General\Input Models\SignUpInput.cs
export interface SignUpInput {
    employeeId: string;
    title: string;
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    role: string;
    linkedInUrl: string;
    birthDate?: Date;
    companyId: number;
    branchId: number;
    departmentId: number;
    countryId: number;
    phoneNumber: string;
    gender: Gender;
    timeZone: string;
    employmentType?: string;

    // When true (default) the backend sends a welcome email to the new user; when false it is suppressed.
    sendWelcomeEmail?: boolean;

    // Document & profile fields
    molListNumber?: string;
    hireDate?: Date;
    probationEndDate?: Date;
    nationalityId?: number;
    passportNumber?: string;
    passportExpiryDate?: Date;
    visaDesignation?: string;
    visaIdNumber?: string;
    visaExpiryDate?: Date;
    fileNumber?: string;
    uidNumber?: string;
    labourCardNumber?: string;
    labourCardExpiryDate?: Date;
    emiratesIdNumber?: string;
    emiratesIdExpiryDate?: Date;
    iqamaNumber?: string;
    iqamaExpiryDate?: string;
    saudiNationalIdNumber?: string;
    qiwaContractNumber?: string;
    muqeemIdNumber?: string;
    visaCategory?: string;
    bankAccountNumber?: string;
    ibanNumber?: string;
    address?: string;

    // Document issue dates — NOT mapped to User, used for fileless attachment creation only
    visaIssueDate?: Date;
    labourCardIssueDate?: Date;
    emiratesIdIssueDate?: Date;

    // Related entity fields (processed separately after User creation)
    degree?: string;
    bankName?: string;
    bankRoutingCode?: string;

    // Salary
    basicSalary?: number;
    houseAllowance?: number;
    transportationAllowance?: number;
    otherAllowance?: number;
}

// ..\mowazaf\Data\General\Input Models\TypeInput.cs
export interface TypeInput {
    name: string;
}

// ..\mowazaf\Data\General\Input Models\UserAttachmentAccessInput.cs
export interface UserAttachmentAccessInput {
    id?: number;
    userId: string;
    accessType: AccessType;
    userAttachmentId?: number;
    userClaimAttachmentId?: number;
}

// ..\mowazaf\Data\General\Input Models\UserAttachmentInput.cs
export interface UserAttachmentInput {
    file?: File | null;
    fileName?: string;
    expiryDate?: string;
    reminderDays?: number;
    userId: string;
    companyAttachmentTypeId?: number;
    description?: string;
    documentNumber?: string;
    issueDate?: string;
    additionalFiles?: File[];
    additionalFileLabels?: string[];
    skipProfileUpdates?: boolean;
}

// ..\mowazaf\Data\General\Input Models\UserDeletionInput.cs
export interface UserDeletionInput {
    name: string;
    email: string;
}

// ..\mowazaf\Data\General\Input Models\UserGroupInput.cs
export interface UserGroupInput {
    id: number;
    userId: string[];
    groupId: number;
}

// ..\mowazaf\Data\General\Input Models\UserHolidayInput.cs
export interface UserHolidayInput {
    userId: string;
    holidayId: number[];
}

// ..\mowazaf\Data\General\Input Models\UserRulesInput.cs
export interface UserRulesInput {
    attendanceImageConfig: UserAttendanceImageConfig;
    faceRecognitionConfig: UserFaceRecognitionConfig;
    workplaceConfig: WorkplaceConfig;
    leaveWithoutScheduleConfig: UserLeaveWithoutScheduleConfig;
    shiftSwapConfig: UserShiftSwapConfig;
    shiftSwapAutoApproveConfig: UserShiftSwapAutoApproveConfig;
    userId: string;
}

// ..\mowazaf\Data\General\Input Models\WelcomeTemplateModel.cs
export interface WelcomeTemplateModel {
    baseUrl?: string;
    companyName?: string;
    token?: string;
    email?: string;
    password?: string;
}

// ..\mowazaf\Data\General\View Models\AttachmentView.cs
export interface AttachmentView {
    id: number;
    fileName?: string;
    fileExtention?: string;
    fileSize?: number;
    filePath?: string;
    fileType?: FileType;
    fileDescription?: string;
    createdOn: Date;
    createdBy?: string;
}

// ..\mowazaf\Data\General\View Models\BranchView.cs
export interface BranchView {
    id: number;
    name: string;
}

// ..\mowazaf\Data\General\View Models\CompanyAttachmentTypeView.cs
export interface CompanyAttachmentTypeView {
    id: number;
    attachmentType: string;
    reminderDays: number;
}

// ..\mowazaf\Data\General\View Models\CompanyRulesView.cs
// Hourly pay (spec §3.3) — nullable, null = server default; see HOURLY_PAY_DEFAULTS.
export interface CompanyRulesView extends CompanyRulesHourlyPayFields {
    id: number;
    attendanceImageConfig: AttendanceImageConfig;
    faceRecognitionConfig: FaceRecognitionConfig;
    shiftFlowRuleType: ShiftFlowRuleType;
    triggerOvertimeVerificationOn: TriggerOvertimeVerificationOn;
    timesheetCreationDay: number;
    hideClockingImagesFromTimesheet: boolean;
    disableAllNotifications: boolean;
    faceRecognitionEnabled: boolean;
    breakConfig: BreakConfig;

    // Overtime verification & auto clock-out timing
    autoClockOutOnUnverifiedOvertime: boolean;
    overtimeVerificationGraceMinutes: number;
    autoClockOutAfterMinutes: number;
    overtimeRecheckIntervalHours: number;
    autoClockOutTimestampMode: AutoClockOutTimestampMode;

    // CO-583 — overtime REQUEST tolerance (non-nullable: the columns always carry an effective
    // value; defaults are 10 / 10).
    overtimeRequestEarlyClockInGraceMinutes: number;
    overtimeRequestLateClockOutGraceMinutes: number;

    // CO-240 — Engagement & Performance job enablement (per company, HR-managed).
    // enableAINudges is the master AI-nudge switch, sourced from Company.EnableAINudges.
    enableRecognitionGapReminder: boolean;
    enableCheckInReminder: boolean;
    enableQ12Nudge: boolean;
    enableAINudges: boolean;

    // CO-354 — asset replenishment notification (type 112). Non-nullable server-side and defaults
    // false; read a missing/undefined value as OFF, never as on.
    enableAssetReplenishmentNotification: boolean;

    // Payroll calculation basis. Null means "not configured" — read it as the documented
    // default (see PAYROLL_BASIS_DEFAULTS), never as zero.
    payrollUseAttendanceOvertime?: boolean | null;
    // CO-280 / CO-304 — pay only approved overtime hours. TRISTATE on the view: true = required,
    // false = the tenant explicitly opted out, null = never configured. Null currently resolves to
    // OFF, but it is NOT the same as an explicit false and the settings UI shows them apart.
    payrollRequireOvertimeApproval?: boolean | null;
    // CO-348 — routing for employees with no able manager. Null = never configured, and resolves
    // to RouteToHr. Shown apart from an explicit RouteToHr, exactly like the tristate above.
    payrollNoManagerOvertimeApproval?: OvertimeNoManagerRouting | null;
    payrollOvertimeBaseMode?: PayrollSalaryBaseMode | null;
    payrollEosBaseMode?: PayrollSalaryBaseMode | null;
    payrollOvertimeRegularMultiplier?: number | null;
    payrollOvertimePremiumMultiplier?: number | null;
    payrollHoursPerMonth?: number | null;

    // Attendance notifications timing (per-company)
    scheduledShiftClockInReminderOffsetMinutes: number;
    scheduledShiftClockOutReminderOffsetMinutes: number;
    scheduledShiftClockInReminderBeforeMinutes?: number | null;
    scheduledShiftClockOutReminderBeforeMinutes?: number | null;
    scheduledShiftEarlyClockInWindowMinutes: number;
    breakInReminderMinutesAfterClockIn?: number | null;
    breakOutReminderMinutesAfterBreakIn: number;
    companyId: number;
    currency: Currency;
    currencyId: number;
    bank: Bank;
    bankId: number;

    // Attendance thresholds
    scheduledShiftAbsentThresholdPercent: number;
    scheduledShiftHalfDayThresholdPercent: number;
    scheduledShiftLateClockInThresholdMinutes: number;
    scheduledShiftEarlyClockOutThresholdMinutes: number;

    // AI Configuration
    aiFeaturesEnabled: boolean;
    hasCustomAIKey: boolean;
    azureOpenAIEndpoint?: string | null;
    azureOpenAIChatDeployment?: string | null;

    // Wialon GPS Vehicle Tracking
    wialonEnabled: boolean;
    hasWialonToken: boolean;

    // Leave-without-schedule + shift-swap policies.
    // leaveWithoutScheduleWorkingDays is a 7-bit mask over JS getDay() (bit 0 = Sunday … bit 6 = Saturday).
    allowLeaveWithoutSchedule: boolean;
    leaveWithoutScheduleWorkingDays: number;
    shiftSwapEnabled: boolean;
    shiftSwapApprovalLimitedToManager: boolean;
    shiftSwapAutoApproveOnPeerAccept: boolean;
    shiftSwapMinAdvanceNoticeHours: number;
    shiftSwapMaxHorizonDays: number;
    shiftSwapMaxActiveRequestsPerUser: number;

    // Geofence / out-of-bounds clocking policy.
    workplaceConfig: WorkplaceConfig;
    outOfBoundsPolicy: OutOfBoundsPolicy;
    noLocationPolicy: NoLocationPolicy;
    workplaceBufferMeters: number;
    notifyManagerOnOutOfBounds: boolean;
    notifyHrOnOutOfBounds: boolean;
    outOfBoundsApprovalLimitedToManager: boolean;
    // Company-configurable quick-fill reason chips for out-of-bounds clocking (effective list; the
    // shared default when the company has not overridden them).
    outOfBoundsReasonPresets?: string[] | null;
}

// ..\mowazaf\Data\General\View Models\CompanyView.cs
export interface CompanyView {
    id: number;
    name: string;
    numberofUsers: number;
    licenseExpireOn: Date;
    plan: string;
    imageUrl: string;
    fax: string;
    address: string;
    email: string;
    phoneNumber: string;
    websiteLink: string;
    rolesMaxUsers: any;
    rolesUsersAvailable: any;
    primaryColor: string;
    attendanceImageConfig: AttendanceImageConfig;
    faceRecognitionConfig: FaceRecognitionConfig;
    shiftFlowRuleType: ShiftFlowRuleType;
    timeZone: string;
    defaultStartTime: string;
    defaultEndTime: string;
    timesheetCreationDay: number;
    status: string;
    /** ⚠️ CO-227 — always 0. Kept on CompanyView for wire compatibility; nothing populates it. */
    trialDays: number;
    hideClockingImagesFromTimesheet: boolean;
    disableAllNotifications: boolean;
    faceRecognitionEnabled: boolean;
    locales: string;
    employerNarrative?: string;
    molNumber?: string;
    bankRoutingCode?: string;
    wpsEmployeeThreshold?: number;
    commercialRegistrationNumber?: string;
    mudadEstablishmentNumber?: string;
    qiwaEntityId?: string;
    gpssaAutoGenerationMode?: GpssaAutoGenerationMode;
    gpssaAutoGenerationDayOfMonth?: number;
    gpssaAutoGenerationLastRunAt?: string | null;
    country: CountryView;
    companyRules: CompanyRulesView;
    organization: OrganizationView;
    subscriptionPlanView: SubscriptionPlanView;
    workplaces: WorkplaceModel[];
}

// ..\mowazaf\Data\General\View Models\CountryView.cs
export interface CountryView {
    id: number;
    name: string;
    isGcc: boolean;
    iso: string;
    nicename: string;
}

// ..\mowazaf\Data\General\View Models\DepartmentView.cs
export interface DepartmentView {
    id: number;
    name: string;
}

// ..\mowazaf\Data\General\View Models\EmailChangedView.cs
export interface EmailChangedView {
    firstName: string;
}

// ..\mowazaf\Data\General\View Models\Error.cs
export interface Error {
    code: string;
    description: string;
    path: string;
    time?: Date;
}

// ..\mowazaf\Data\General\View Models\GroupRulesView.cs
export interface GroupRulesView {
    id: number;
    attendanceImageConfig: AttendanceImageConfig;
    faceRecognitionConfig: FaceRecognitionConfig;
    shiftFlowRuleType: ShiftFlowRuleType;
    triggerOvertimeVerificationOn: TriggerOvertimeVerificationOn;
    timesheetCreationDay: number;
    hideClockingImagesFromTimesheet: boolean;
    disableAllNotifications: boolean;
    faceRecognitionEnabled: boolean;
    breakConfig: BreakConfig;
    groupId: number;
    currency: Currency;
    currencyId: number;
    bank: Bank;
    bankId: number;
}

// ..\mowazaf\Data\General\View Models\GroupView.cs
export interface GroupView {
    id: number;
    name: string;
}

// ..\mowazaf\Data\General\View Models\HolidayConfigView.cs
export interface HolidayConfigView {
    id: number;
    enabled: boolean;
    date: Date;
    holidayId: number;
    holiday: Holiday;
    companyId: number;
    company: Company;
    countryId: number;
    country: Country;
}

// ..\mowazaf\Data\General\View Models\HolidayNotificationView.cs
export interface HolidayNotificationView {
    holidayId: number;
    holidayName: string;
    date: Date;
    dateString: string;
    baseUrl: string;
    timezoneId: string;
    companyId: number;
    countryId: number;
    currentUserFirstName: string;
}

// ..\mowazaf\Data\General\View Models\HolidaysConfigView.cs
export interface HolidaysConfigView {
    holidayConfigInputList: HolidayConfigView[];
    holidaysCount: number;
    countriesCount: number;
}

// ..\mowazaf\Data\General\View Models\HolidayView.cs
export interface HolidayView {
    id: number;
    name: string;
    description: string;
    date: Date;
    holidayType?: string;
    locations?: string;
    recurrence?: string;
    isMandatory?: boolean;
}

// ..\mowazaf\Data\General\View Models\LogAttachmentView.cs
export interface LogAttachmentView {
    id: number;
    name: string;
    extention: string;
    byteLength: number;
    path: string;
    createdOn: Date;
    logId: number;
}

// ..\mowazaf\Data\General\View Models\LoginResponse.cs
export interface LoginResponse {
    accessToken: string;
    accessTokenExpiresIn: number;
    refreshTokenExpiresIn: number;
    firstLogin: boolean;
}

// ..\mowazaf\Data\General\View Models\LogView.cs
export interface LogView {
    id: number;
    message: string;
    logType: string;
}

// ..\mowazaf\Data\General\View Models\NotificationsResponseView.cs
export interface NotificationsResponseView {
    notificationsTotalCount: number;
    notificationsTotalUnreadCount: number;
    // Server-side this is now List<NotificationView> (see NotificationView below): the wire no longer
    // carries ccIds, excludedToAndCcIds, emailTemplate, idempotencyKey or fromId, and dataJson is
    // sanitized down to MatchId/ShiftId/UserId/Code/OtpCode. Typed as Notification for backward
    // compatibility with the existing consumers — every field that stopped shipping is optional here and
    // is read by nothing, and `Notification` now carries actionSource/actionEntityId too.
    notificationsList: Notification[];
}


// ..\mowazaf\Data\General\View Models\OrganizationView.cs
export interface OrganizationView {
    id: number;
    name: string;
    numberofUsers: number;
}

// ..\mowazaf\Data\General\View Models\PaginationView.cs
export interface PaginationView<T> {
    results: T[];
    offset: number;
    count: number;
}

// ..\mowazaf\Data\General\View Models\Result.cs
export interface Result<TPayload> {
    payload: TPayload;
    status: number;
    errors: Error[];
}

// ..\mowazaf\Data\General\View Models\SubscriptionPlanView.cs
export interface SubscriptionPlanView {
    id: number;
    name: string;
    monthlyPricePerUser?: number;
    yearlyPricePerUser?: number;
    duration?: string;
}

// ..\mowazaf\Data\General\View Models\TimeZoneInfoView.cs
export interface TimeZoneInfoView {
    id: string;
    displayName: string;
    baseUtcOffset: string;
    standardName: string;
}

// ..\mowazaf\Data\General\View Models\Token.cs
export interface Token {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    firstLogin: boolean;
}

// ..\mowazaf\Data\General\View Models\TrialExpiryView.cs
export interface TrialExpiryView {
    companyId: number;
}

// ..\mowazaf\Data\General\View Models\UserAttachmentAccessView.cs
export interface UserAttachmentAccessView {
    id: number;
    accessType: AccessType;
    createdOn: Date;
    createdById?: string;
    userId?: string;
}

// ..\mowazaf\Data\General\View Models\UserAttachmentDetailsView.cs
export interface UserAttachmentDetailsView {
    fileContent?: Uint8Array;
    fileName?: string;
    contentType?: string;
    filePath: string;
    fileExtension: string;
}

// ..\mowazaf\Data\General\View Models\UserAttachmentView.cs
export interface UserAttachmentView {
    id: number;
    userId: string;
    companyAttachmentTypeId: number;
    companyAttachmentType?: CompanyAttachmentType;
    fileName?: string;
    fileExtention?: string;
    fileSize?: number;
    filePath?: string;
    fileType?: FileType;
    fileDescription?: string;
    expiryDate?: string;
    reminderDays?: number;
    documentNumber?: string;
    issueDate?: string;
    createdOn?: string;
    additionalFiles?: AttachmentFileView[];
}

// ..\mowazaf\Data\General\View Models\AttachmentFileView.cs
export interface AttachmentFileView {
    id: number;
    fileName?: string;
    fileExtention?: string;
    fileSize: number;
    fileType?: FileType;
    label?: string;
    sortOrder: number;
}

// ..\mowazaf\Data\General\View Models\FileAnalysisResultView.cs
export interface FileAnalysisResult {
    ocrSuccess: boolean;
    detectedDocumentType?: string;
    matchedAttachmentTypeId?: number;
    matchedAttachmentTypeName?: string;
    documentTypeConfidence: number;
    detectedExpiryDate?: string;
    expiryDateConfidence: number;
    detectedEmployeeName?: string;
    errorMessage?: string;
    extractedDocumentNumber?: string;
    extractedIssueDate?: string;
    extractedNationality?: string;
    extractedDateOfBirth?: string;
    extractedGender?: string;
    extractedFullName?: string;
    extractedOccupation?: string;
    extractedMaritalStatus?: string;
    extractedPassportNumber?: string;
    extractedEmiratesIdNumber?: string;
    extractedUnifiedNumber?: string;
    extractedMohrePermitNumber?: string;
}

// ..\mowazaf\Data\General\View Models\UserGroupView.cs
export interface UserGroupView {
    id: number;
    group: Group;
    user: UserView;
}

// ..\mowazaf\Data\General\View Models\UserRulesView.cs
export interface UserRulesView {
    id: number;
    attendanceImageConfig: UserAttendanceImageConfig;
    faceRecognitionConfig: UserFaceRecognitionConfig;
    workplaceConfig: WorkplaceConfig;
    leaveWithoutScheduleConfig: UserLeaveWithoutScheduleConfig;
    shiftSwapConfig: UserShiftSwapConfig;
    shiftSwapAutoApproveConfig: UserShiftSwapAutoApproveConfig;
    userCurrency: UserCurrency;
    currencyId: number;
    currency: Currency;
    userBank: UserBank;
    bankId: number;
    bank: Bank;
    userId: string;
    user: UserView;
}

// ..\mowazaf\Data\General\View Models\UserView.cs
export interface UserView {
    id: string;
    employeeId: string;
    userName: string;
    email: string;
    imageUrl: string;
    company: UserCompanyView;
    /** PRIMARY role only (RoleTags.PrimaryRole server-side): the first NON-TAG role over a list with no
     *  guaranteed order. Fine for display. NEVER build an authorization-shaped gate on it — see `roles`. */
    role: string;
    /** EVERY role the user holds, tags included. Use this for any client gate that must agree with a
     *  server-side role MEMBERSHIP test; `role` alone diverges as soon as a user holds two non-tag roles. */
    roles?: string[];
    country: CountryView;
    // Derived server-side from Country.IsGCC (tenant country first, user country as fallback).
    // Drives the Sunday-start week in the attendance calendar.
    isGCCCountry: boolean;
    title: string;
    firstName: string;
    lastName: string;
    fullName: string;
    linkedInUrl: string;
    birthDate?: Date;
    branchId?: number;
    branch: BranchView;
    departmentId?: number;
    department: DepartmentView;
    phoneNumber: string;
    currentWorkingDuration: number;
    timeZone: string;
    locale: string;
    fiscalYearStartingMonth: number;
    isManager: boolean;
    isAssetManager?: boolean;
    currentStatus: UserCurrentStatus;
    currentCustomStatusText: string;
    userRulesId: number;
    userRules: UserRulesView;
    workplaces: WorkplaceModel[];
    managerIds: string[];
    managerNames?: string[];
    hireDate: Date;
    probationEndDate?: Date;
    employmentStatus?: 'Active' | 'OnProbation' | 'Onboarding' | 'Offboarding' | 'Terminated' | 'Resigned';
    lastWorkingDay?: Date;
    terminationDate?: Date;
    rehireEligible?: boolean;
    createdOn: Date;
    isArchived: boolean;
    userFaceImage: UserFaceImage;
    gender?: Gender;
    maritalStatus?: MaritalStatus;
    employeeUniqueId?: string;
    ibanNumber?: string;
    vehicleId?: string;
    employmentType?: string;
    molListNumber?: string;
    fileNumber?: string;
    uidNumber?: string;
    visaDesignation?: string;
    bankAccountNumber?: string;
    address?: string;
    nationalityId?: number;
    nationality?: CountryView;
    emirate?: string;
    passportNumber?: string;
    passportExpiryDate?: Date;
    emiratesIdNumber?: string;
    emiratesIdExpiryDate?: Date;
    labourCardNumber?: string;
    labourCardExpiryDate?: Date;
    visaIdNumber?: string;
    visaExpiryDate?: Date;
    iqamaNumber?: string;
    iqamaExpiryDate?: string;
    saudiNationalIdNumber?: string;
    qiwaContractNumber?: string;
    muqeemIdNumber?: string;
    visaCategory?: string;
    personalEmail?: string;
    userEducations?: UserEducationView[];
    // Welcome-email lifecycle (surfaced by account/WelcomeEmailStatus).
    // welcomeEmailStatus: "NotSent" | "Suppressed" | "Sent" | "Failed".
    welcomeEmailStatus?: string;
    // ISO datetime; omitted by the server when null.
    welcomeEmailSentAt?: string;
    // ISO datetime of the user's own password change; present/non-null ⇒ the user has established
    // their own credentials, null/absent ⇒ still on the admin-issued temporary password.
    passwordChangedAt?: string;
    // false = user has already set their own password (a resend rotates it — warn before sending).
    mustChangePasswordOnNextLogin?: boolean;
    // Hourly pay (docs/hourly-pay-spec.md §3.1 / §7.2). Optional: an older server omits it, and an
    // absent value MUST read as 'Salaried' so the hourly surfaces stay dark.
    payBasis?: PayBasis;
    // §7.2 — the company switch, as GetUserInfo reports it. The spec does not pin whether it rides
    // the user or `company`, so clients read both (see utils/hourlyPayAccess.ts).
    hourlyPayEnabled?: boolean;
}

// ..\mowazaf\Data\General\View Models\UserView.UserCompanyView.cs
export interface UserCompanyView {
    id: number;
    name: string;
    numberofUsers: number;
    licenseExpireOn: Date;
    plan: string;
    imageUrl: string;
    primaryColor: string;
    locales: string;
    attendanceImageConfig: AttendanceImageConfig;
    faceRecognitionConfig: FaceRecognitionConfig;
    hideClockingImagesFromTimesheet: boolean;
    faceRecognitionEnabled: boolean;
    // Company-wide shift-swap default. Combined with UserRulesView.shiftSwapConfig to gate the nav item.
    shiftSwapEnabled: boolean;
    // CO-239 — the ENVIRONMENT-level billing master switch (server-side `Billing:Enabled`), not a
    // per-company setting. Gates the Billing nav item (useFilteredNavigation) and the /settings/billing
    // route (App.tsx). Optional because a redux-persist snapshot written before this field existed will
    // not carry it — and `undefined` must read as DISABLED (fail closed), since the whole point is to
    // keep a legacy perpetual tenant off a billing page that reports nonsense while billing is inert.
    billingEnabled?: boolean;
    // Hourly pay master switch (spec §7.2). Optional + absent = off.
    hourlyPayEnabled?: boolean;
    currencyId: number;
    currency: Currency;
    complianceCountry: ComplianceCountry;
    molNumber?: string;
    bankRoutingCode?: string;
    wpsEmployeeThreshold: number;
    employerNarrative?: string;
    commercialRegistrationNumber?: string;
    mudadEstablishmentNumber?: string;
    qiwaEntityId?: string;
}

// ..\mowazaf\Data\General\View Models\UserViewSimple.cs
export interface UserViewSimple {
    id: string;
    userName: string;
    email: string;
    imageUrl: string;
    companyId: number;
    role: string;
    title: string;
    firstName: string;
    lastName: string;
    fullName: string;
    phoneNumber: string;
    managerIds: string[];
}

// ..\mowazaf\Data\General\View Models\UserWeekendInput.cs
export interface UserWeekendInput {
    userId: string;
    listOfWeekend: number[];
}

// ..\mowazaf\Data\General\View Models\WorkplaceModel.cs
export interface LatLng {
    lat: number;
    lng: number;
}

// ..\mowazaf\Data\General\View Models\WorkplaceModel.cs
export interface WorkplaceModel {
    id: number;
    // Optional display name for the site (e.g. "HQ", "Riyadh branch"). Backend-supplied and may be
    // absent on older payloads; when present, the clock dialog names the nearest site in the out-of-bounds
    // banner ("You're about 250 m outside HQ"). When empty, it falls back to "the allowed area".
    name?: string;
    bounds: LatLng[];
}

// AI Chat Models

/**
 * A deep-link the assistant can surface beneath its reply (e.g. when answering
 * "where do I request leave?"). `route` is an internal SPA path, already
 * role-filtered server-side; `icon` is a lucide icon name; `label` is the
 * backend-provided fallback label used when the route has no navigation match.
 */
export interface AINavigationItem {
    label: string;
    route: string;
    icon: string;
}

export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: Date;
    isError?: boolean;
    /** Optional deep-links surfaced under an assistant reply. */
    navigation?: AINavigationItem[];
}

export interface ChatSession {
    conversationId: number | null;
    messages: ChatMessage[];
}

export interface AIAssistantState {
    isOpen: boolean;
    isMinimized: boolean;
    isLoading: boolean;
    isConfigured: boolean;
    availableToolsCount: number;
    statusMessage: string;
    currentSession: ChatSession;
    error: string | null;
    /** CO-225 daily AI request quota, as reported by AI/Status. Null until Status has answered. */
    quota: AIQuotaState | null;
}

/**
 * CO-225 — the caller's daily AI request allowance.
 * `exempt` covers Operator, companies on their own Azure OpenAI key, and AI being switched off.
 * When it is true the UI must render NOTHING rather than a fabricated "0 of 0".
 */
export interface AIQuotaState {
    exempt: boolean;
    used: number;
    limit: number;
    /** ISO-8601 UTC instant of the next reset (UTC midnight, which is 03:00 in Riyadh). */
    resetsAtUtc: string;
}

// ── Terms & Policies ────────────────────────────────────────────
export interface TermsAndPoliciesView {
    id: number;
    companyId: number;
    version: number;
    content: string;
    isPublished: boolean;
    publishedAt?: string;
    publishedByUserId?: string;
    publishedByUserName?: string;
    createdOn: string;
    updatedOn?: string;
}

export interface UpdateTermsAndPoliciesInput {
    content: string;
}

export interface MyTermsStatusView {
    hasPublishedTerms: boolean;
    currentVersion?: number;
    hasAccepted: boolean;
    acceptedVersion?: number;
    acceptedAt?: string;
}

export interface UserTermsStatusView {
    userId: string;
    fullName?: string;
    email?: string;
    employeeId?: string;
    hasPublishedTerms: boolean;
    currentVersion?: number;
    hasAccepted: boolean;
    acceptedVersion?: number;
    acceptedAt?: string;
    isStale: boolean;
}

export type AcceptanceStatusFilter = 'all' | 'accepted' | 'notAccepted' | 'stale';

export interface AcceptanceFilterInput {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: AcceptanceStatusFilter;
}

export interface AcceptanceRowView {
    userId: string;
    fullName?: string;
    email?: string;
    employeeId?: string;
    hasAccepted: boolean;
    acceptedVersion?: number;
    acceptedAt?: string;
    isStale: boolean;
}

export interface AcceptanceListPage {
    items: AcceptanceRowView[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
}

export interface AcceptanceListView {
    hasPublishedTerms: boolean;
    currentVersion?: number;
    totalEmployees: number;
    acceptedOnCurrent: number;
    page: AcceptanceListPage;
}

export interface TermsVersionView {
    version: number;
    content: string;
    publishedAt?: string;
    publishedByUserName?: string;
}
